import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Optional,
    Output,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    NgbTypeahead,
    NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

export type TypeaheadSearchFn<T = Record<string, unknown>> = (
    term: string
) => Observable<readonly T[]>;

export type TypeaheadResolveFn<T = Record<string, unknown>> = (
    value: string | number
) => Observable<T | null>;

@Component({
    selector: 'app-input-typeahead',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgbTypeahead],
    templateUrl: './input-typeahead.component.html',
    styleUrls: ['./input-typeahead.component.css'],
    host: {
        '[class.compact]': 'compact',
    },
})
export class InputTypeaheadComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input() inputId = '';
    @Input() placeholder = 'Buscar...';
    @Input() required = false;
    @Input() disabled = false;
    @Input() submitted = false;
    @Input() requiredError: string | null = null;
    @Input() compact = false;
    @Input() styleClasses = 'form-control';

    /** Campo del ítem que se guarda en el formulario (p. ej. `id`). */
    @Input() valueKey = 'id';
    /** Campos mostrados en input y sugerencias, separados por coma. */
    @Input() displayKeys: string[] = ['nombre'];
    @Input() minSearchLength = 1;
    @Input() debounceMs = 300;

    /** Búsqueda remota o filtrado en servidor. */
    @Input() searchFn?: TypeaheadSearchFn;

    /** Opciones estáticas; si no hay `searchFn`, filtra en cliente. */
    @Input() options: ReadonlyArray<Record<string, unknown>> = [];

    /** Resuelve etiqueta cuando el control solo tiene el id. */
    @Input() resolveFn?: TypeaheadResolveFn;

    /** Emite el valor del formulario (id) al seleccionar o limpiar. */
    @Output() valueChange = new EventEmitter<string | number | null>();

    readonly inputControl = new FormControl<string>(
        { value: '', disabled: false },
        { nonNullable: true }
    );

    searching = false;
    searchFailed = false;

    private boundValue: string | number | null = null;
    private selectedItem: Record<string, unknown> | null = null;
    private blurred = false;
    private onChange: (value: string | number | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(@Optional() @Self() public ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.requiredError =
            this.requiredError ?? `Campo ${this.label} requerido`;
    }

    get invalid(): boolean {
        return !!this.ngControl?.control?.invalid;
    }

    get touched(): boolean {
        return this.blurred || !!this.ngControl?.touched;
    }

    get showError(): boolean {
        return (this.submitted || this.touched) && this.invalid;
    }

    get resultFields(): string[] {
        return this.displayKeys.length
            ? this.displayKeys
            : ['nombre'];
    }

    typeaheadSearch: OperatorFunction<string, readonly unknown[]> = (
        text$
    ) =>
        text$.pipe(
            debounceTime(this.debounceMs),
            distinctUntilChanged(),
            tap(() => {
                this.searching = true;
                this.searchFailed = false;
            }),
            switchMap((rawTerm) => {
                const term = String(rawTerm ?? '').trim();
                if (term.length < this.minSearchLength) {
                    return of([]);
                }
                if (this.searchFn) {
                    return this.searchFn(term).pipe(
                        map((items) =>
                            this.filterItemsByTerm(
                                items as ReadonlyArray<
                                    Record<string, unknown>
                                >,
                                term
                            )
                        ),
                        catchError(() => {
                            this.searchFailed = true;
                            return of([]);
                        })
                    );
                }
                return of(this.filterLocalOptions(term));
            }),
            tap(() => {
                this.searching = false;
            })
        );

    formatter = (item: unknown): string => this.formatItem(item);

    resultFormatter = (item: unknown): string => this.formatItem(item);

    writeValue(value: string | number | Record<string, unknown> | null): void {
        if (value == null || value === '') {
            this.boundValue = null;
            this.selectedItem = null;
            this.inputControl.setValue('', { emitEvent: false });
            return;
        }

        if (typeof value === 'object') {
            this.selectedItem = value;
            this.boundValue = this.readValueKey(value);
            this.inputControl.setValue(this.formatItem(value), {
                emitEvent: false,
            });
            return;
        }

        this.boundValue = value;

        if (this.resolveFn) {
            this.resolveFn(value).subscribe({
                next: (item) => {
                    if (item) {
                        this.selectedItem = item;
                        this.inputControl.setValue(this.formatItem(item), {
                            emitEvent: false,
                        });
                    } else {
                        this.inputControl.setValue(String(value), {
                            emitEvent: false,
                        });
                    }
                },
                error: () => {
                    this.inputControl.setValue(String(value), {
                        emitEvent: false,
                    });
                },
            });
            return;
        }

        this.inputControl.setValue(String(value), { emitEvent: false });
    }

    registerOnChange(fn: (value: string | number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.inputControl.disable({ emitEvent: false });
        } else {
            this.inputControl.enable({ emitEvent: false });
        }
    }

    onSelect(event: NgbTypeaheadSelectItemEvent): void {
        const item = event.item as Record<string, unknown>;
        this.selectedItem = item;
        this.emitValue(this.readValueKey(item));
        this.inputControl.setValue(this.formatItem(item), { emitEvent: false });
    }

    onBlur(): void {
        this.blurred = true;
        this.onTouched();
        this.syncInputWithSelection();
    }

    onInputChange(): void {
        const text = String(this.inputControl.value ?? '').trim();
        if (!text) {
            this.clearSelection();
        }
    }

    private syncInputWithSelection(): void {
        if (this.selectedItem) {
            this.inputControl.setValue(this.formatItem(this.selectedItem), {
                emitEvent: false,
            });
            return;
        }
        if (this.boundValue != null && this.boundValue !== '') {
            this.inputControl.setValue(String(this.boundValue), {
                emitEvent: false,
            });
        }
    }

    private clearSelection(): void {
        this.selectedItem = null;
        this.emitValue(null);
    }

    private emitValue(value: string | number | null): void {
        this.boundValue = value;
        this.onChange(value);
        this.valueChange.emit(value);
    }

    private filterLocalOptions(
        term: string
    ): readonly Record<string, unknown>[] {
        return this.filterItemsByTerm(this.options, term);
    }

    private filterItemsByTerm(
        items: ReadonlyArray<Record<string, unknown>>,
        term: string
    ): readonly Record<string, unknown>[] {
        const normalizedTerm = this.normalizeForSearch(term);
        if (!normalizedTerm) {
            return items;
        }
        return items.filter((option) =>
            this.normalizeForSearch(this.formatItem(option)).includes(
                normalizedTerm
            )
        );
    }

    private normalizeForSearch(text: string): string {
        return String(text ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    }

    private formatItem(item: unknown): string {
        if (item == null) {
            return '';
        }
        if (typeof item === 'string' || typeof item === 'number') {
            return String(item);
        }
        const record = item as Record<string, unknown>;
        return this.resultFields
            .map((key) => record[key])
            .filter((part) => part != null && String(part).trim() !== '')
            .map((part) => String(part))
            .join(', ');
    }

    private readValueKey(item: Record<string, unknown>): string | number | null {
        const raw = item[this.valueKey];
        if (raw == null || raw === '') {
            return null;
        }
        if (typeof raw === 'string' || typeof raw === 'number') {
            return raw;
        }
        return String(raw);
    }
}
